/**
 * AIChatBot Component
 * The main floating chat window that appears across all pages.
 * Features:
 * - Draggable (click and drag header)
 * - Resizable (drag bottom-right corner)
 * - Persists across route changes
 * - Professional chat UI matching the app theme
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAIContext } from './AIContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import ChatInput from './ChatInput';
import ConfirmationCard from './ConfirmationCard';
import MessageBubble from './MessageBubble';
import RobotIcon from '../icons/RobotIcon';

// Storage keys
const POSITION_STORAGE_KEY = 'balancio_ai_position';
const SIZE_STORAGE_KEY = 'balancio_ai_size';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

const DEFAULT_POSITION: Position = { x: 0, y: 0 };
const DEFAULT_SIZE: Size = { width: 380, height: 540 };
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const MIN_HEIGHT = 380;
const MAX_HEIGHT = 700;

const AIChatBot: React.FC = () => {
  const { isOpen, closeChat, clearChat, messages } = useAIContext();
  const { isDark } = useThemeContext();

  // Position and size state
  // We always store an offset from a base position.
  // Default is (0,0) meaning no offset from the initial CSS (right: 1.5rem, bottom: 1.5rem).
  // When dragged, we store the cumulative offset.
  // This avoids toggling between right/bottom and transform modes.
  const [position, setPosition] = useState<Position>(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { x: parsed.x || 0, y: parsed.y || 0 };
      }
    } catch {}
    return DEFAULT_POSITION;
  });

  const [size, setSize] = useState<Size>(() => {
    try {
      const saved = localStorage.getItem(SIZE_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          width: Math.min(Math.max(parsed.width || DEFAULT_SIZE.width, MIN_WIDTH), MAX_WIDTH),
          height: Math.min(Math.max(parsed.height || DEFAULT_SIZE.height, MIN_HEIGHT), MAX_HEIGHT),
        };
      }
    } catch {}
    return DEFAULT_SIZE;
  });

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStart = useRef<{ x: number; y: number; pos: Position }>({ x: 0, y: 0, pos: { x: 0, y: 0 } });
  const resizeStart = useRef<{ x: number; y: number; size: Size }>({ x: 0, y: 0, size: { width: 0, height: 0 } });
  const chatRef = useRef<HTMLDivElement>(null);

  // Persist position and size, and notify FloatingButton
  useEffect(() => {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
    // Dispatch custom event so FloatingButton knows the position changed
    window.dispatchEvent(new CustomEvent('balancio-position-changed', {
      detail: position,
    }));
  }, [position]);

  useEffect(() => {
    localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(size));
  }, [size]);

  // Listen for reset position event from FloatingButton
  useEffect(() => {
    const handleResetPosition = () => {
      setPosition({ x: 0, y: 0 });
    };
    window.addEventListener('balancio-reset-position', handleResetPosition);
    return () => window.removeEventListener('balancio-reset-position', handleResetPosition);
  }, []);

  // ============ Drag Logic ============

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.chat-header')) {
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        pos: { ...position },
      };
      e.preventDefault();
    }
  }, [position]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      size: { ...size },
    };
    e.preventDefault();
  }, [size]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPosition({
          x: dragStart.current.pos.x + dx,
          y: dragStart.current.pos.y + dy,
        });
      }

      if (isResizing) {
        const dx = e.clientX - resizeStart.current.x;
        const dy = e.clientY - resizeStart.current.y;
        setSize({
          width: Math.min(Math.max(resizeStart.current.size.width + dx, MIN_WIDTH), MAX_WIDTH),
          height: Math.min(Math.max(resizeStart.current.size.height + dy, MIN_HEIGHT), MAX_HEIGHT),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing]);

  // ============ Auto scroll to bottom ============
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // ============ Check if mobile ============
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile, disable dragging/resizing and use full screen
  if (!isOpen) return null;

  return (
    <div
      ref={chatRef}
      className={`fixed z-50 flex flex-col overflow-hidden transition-shadow duration-300 ${
        isMobile ? 'rounded-none' : 'rounded-2xl'
      }`}
      style={{
        width: isMobile ? '100vw' : size.width,
        height: isMobile ? '100dvh' : size.height,
        right: isMobile ? '0' : '1.5rem',
        bottom: isMobile ? '0' : '1.5rem',
        left: isMobile ? 0 : undefined,
        top: isMobile ? 0 : undefined,
        transform: !isMobile ? `translate(${position.x}px, ${position.y}px)` : undefined,
        backgroundColor: isDark ? 'var(--light)' : '#FFFFFF',
        border: isMobile ? 'none' : '1px solid',
        borderColor: isMobile ? 'none' : 'var(--grey-light)',
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: isDragging ? 'none' : undefined,
        borderRadius: isMobile ? '0px' : undefined,
      }}
      onMouseDown={isMobile ? undefined : handleMouseDown}
    >
      {/* Header - Draggable on desktop */}
      <div
        className={`flex items-center justify-between px-4 py-3 select-none ${
          isMobile ? '' : 'chat-header cursor-grab active:cursor-grabbing'
        }`}
        style={{
          backgroundColor: isDark ? 'var(--bg-grey-light)' : 'var(--primary)',
          color: isDark ? 'var(--dark)' : '#FFFFFF',
        }}
      >
        <div className="flex items-center gap-2">
          <RobotIcon size={20} />
          <span className="font-semibold text-sm">Balancio AI</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Clear chat button */}
          {messages.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearChat();
              }}
              className="p-1.5 rounded-lg transition-all hover:opacity-70"
              title="Clear conversation"
              aria-label="Clear conversation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
            </button>
          )}
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeChat();
            }}
            className="p-1.5 rounded-lg transition-all hover:opacity-70"
            title="Close"
            aria-label="Close chat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div
        className="flex-1 overflow-y-auto px-3 py-2 min-h-0"
        style={{
          backgroundColor: isDark ? 'var(--light)' : '#FFFFFF',
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--grey-dark) transparent',
        }}
      >
        {messages.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center h-full text-center px-6"
            style={{ color: 'var(--grey-dark)' }}
          >
            <div className="mb-4 opacity-40" style={{ color: 'var(--primary)' }}>
              <RobotIcon size={48} />
            </div>
            <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--dark)' }}>
              Hello! I'm Balancio AI
            </h3>
            <p className="text-sm leading-relaxed">
              I can help you with:
            </p>
            <div className="mt-3 text-xs text-left space-y-1.5">
              <p>View your financial data and KPIs</p>
              <p>Create and manage transactions</p>
              <p>Navigate to any page</p>
              <p>Answer questions about accounting</p>
              <p>Store important information</p>
            </div>
            <p className="mt-4 text-xs opacity-60">
              Try asking: "Show my balance sheet" or "Create a new transaction"
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Confirmation Card */}
      <ConfirmationCard />

      {/* Input Area */}
      <ChatInput />

      {/* Resize Handle - only show on desktop */}
      {!isMobile && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeMouseDown}
          style={{
            borderRight: '3px solid',
            borderBottom: '3px solid',
            borderColor: 'var(--grey-dark)',
            borderBottomRightRadius: '4px',
            opacity: 0.4,
          }}
        />
      )}
    </div>
  );
};

export default AIChatBot;
